import { useLayoutEffect, useRef } from 'react';

/**
 * Gentle horizontal auto-scroll (`scrollLeft`). Pauses on touch / pointer drag; resumes after `resumeAfterMs`.
 * Wheel is ignored so vertical page scroll does not pause drift. At the end, jumps to 0.
 * Parent should cap width (e.g. max-w) so one row of cards still overflows on large screens.
 * @param {{ direction?: 1 | -1, speed?: number, resumeAfterMs?: number }} opts
 */
export function useAutoScrollCarousel({ direction = 1, speed = 0.38, resumeAfterMs = 2800 }) {
  const viewportRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);

  useLayoutEffect(() => {
    let raf = 0;
    let boundEl = null;

    const clearResume = () => {
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
    };

    const onInteractStart = () => {
      pausedRef.current = true;
      clearResume();
    };

    const onInteractEnd = () => {
      clearResume();
      resumeTimerRef.current = setTimeout(() => {
        pausedRef.current = false;
        resumeTimerRef.current = null;
      }, resumeAfterMs);
    };

    const unbind = () => {
      if (!boundEl) return;
      boundEl.removeEventListener('touchstart', onInteractStart);
      boundEl.removeEventListener('touchend', onInteractEnd);
      boundEl.removeEventListener('touchcancel', onInteractEnd);
      boundEl.removeEventListener('pointerdown', onInteractStart);
      boundEl.removeEventListener('pointerup', onInteractEnd);
      boundEl.removeEventListener('pointerleave', onInteractEnd);
      boundEl = null;
    };

    const bind = (el) => {
      if (!el || boundEl === el) return;
      unbind();
      boundEl = el;
      el.addEventListener('touchstart', onInteractStart, { passive: true });
      el.addEventListener('touchend', onInteractEnd, { passive: true });
      el.addEventListener('touchcancel', onInteractEnd, { passive: true });
      el.addEventListener('pointerdown', onInteractStart);
      el.addEventListener('pointerup', onInteractEnd);
      el.addEventListener('pointerleave', onInteractEnd);
    };

    const tick = () => {
      const el = viewportRef.current;
      if (!el) {
        if (boundEl) unbind();
      } else {
        bind(el);
        if (!pausedRef.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const maxScroll = el.scrollWidth - el.clientWidth;
          if (maxScroll > 0.5) {
            if (direction > 0) {
              let next = el.scrollLeft + speed;
              if (next >= maxScroll - 0.5) next = 0;
              el.scrollTo({ left: next, top: 0, behavior: 'auto' });
            } else {
              let next = el.scrollLeft - speed;
              if (next <= 0.5) next = maxScroll;
              el.scrollTo({ left: next, top: 0, behavior: 'auto' });
            }
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      clearResume();
      unbind();
    };
  }, [direction, speed, resumeAfterMs]);

  return viewportRef;
}
